import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { authService } from '../../services/auth';
import { i18nService } from '../../services/i18n';
import { notifyCreditRefresh, subscribeCreditRefresh } from '../../services/creditRefresh';
import { CampaignClaimError, campaignActionUrl, campaignDismissKey, isCampaignActive, type CampaignAction, type CampaignSlot, type CampaignView } from '../../services/campaign';

/** 外层按认证对象隔离内部状态，旧账号的领取结果、关闭记录不会进入新账号。 */
export default function CampaignCard({ slot }: { slot: CampaignSlot }) {
  const auth = useSyncExternalStore(callback => authService.subscribe(callback), () => authService.getAuthState());
  if (!auth) return null;
  const key = authService.getSessionIdentity();
  return <CampaignCardContent key={key} slot={slot} userId={String(auth.user.id)} />;
}

/** 单个广告位复用平台 DTO；不会自动领取，领取失败后只查询最新资格。 */
function CampaignCardContent({ slot, userId }: { slot: CampaignSlot; userId: string }) {
  const [campaign, setCampaign] = useState<CampaignView | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [hidden, setHidden] = useState(false);
  const sequence = useRef(0), alive = useRef(false), claiming = useRef(false);
  const base = authService.getBaseUrl();
  const t = (key: string) => i18nService.t(`campaign_${key}`);
  /** 读缓存不影响活动时效，平台不可达时不继续显示旧资格。 */
  const refresh = async (force = true) => {
    if (claiming.current) return;
    const request = ++sequence.current;
    try {
      const slots = await authService.campaigns.active(force);
      if (!alive.current || request !== sequence.current) return;
      const card = slots[slot] ?? null;
      let dismissed = false;
      try { dismissed = Boolean(card && localStorage.getItem(campaignDismissKey(card, base, userId))); } catch { /* 禁用存储不影响使用。 */ }
      setCampaign(card); setHidden(dismissed);
    } catch { if (alive.current && request === sequence.current) setCampaign(current => current?.claim?.claimed_today ? current : null); }
  };
  useEffect(() => {
    alive.current = true;
    void refresh(false);
    const focus = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', focus);
    const timer = window.setInterval(focus, 30 * 60_000);
    const unsubscribe = subscribeCreditRefresh(focus);
    return () => { alive.current = false; sequence.current++; window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', focus); window.clearInterval(timer); unsubscribe(); };
  }, [slot, userId, base]);
  useEffect(() => {
    if (campaign?.end_at && campaign.end_at * 1000 <= Date.now()) { setCampaign(null); return; }
    const deadlines = [campaign?.end_at, campaign?.claim?.next_claim_at].filter((value): value is number => Boolean(value && value * 1000 > Date.now()));
    if (!deadlines.length) return;
    const remaining = Math.min(...deadlines) * 1000 - Date.now();
    if (remaining <= 0) { setCampaign(null); return; }
    // 浏览器定时器有 32 位上限，长活动靠定期刷新重新安排到期检查。
    const timer = window.setTimeout(() => void refresh(), Math.min(remaining, 2_147_000_000));
    return () => window.clearTimeout(timer);
  }, [campaign]);

  /** 关闭仅记录当前账号的活动期次，不取消用户已发出的领取请求。 */
  const dismiss = () => {
    if (!campaign) return;
    try { localStorage.setItem(campaignDismissKey(campaign, base, userId), '1'); } catch { /* 本次仍然关闭。 */ }
    setHidden(true);
  };
  /** 站内路由使用白名单，领取状态只采信平台返回。 */
  const perform = async (action: CampaignAction) => {
    if (!campaign || !isCampaignActive(campaign)) { void refresh(); return; }
    if (action.type === 'route' && action.route) {
      window.dispatchEvent(new CustomEvent('campaign:navigate', { detail: action.route })); return;
    }
    if (action.type !== 'claim' || !campaign.claim?.can_claim || claiming.current) return;
    claiming.current = true; sequence.current++; setBusy(true); setMessage('');
    try {
      const result = await authService.campaigns.claim(campaign.id);
      if (!alive.current) return;
      setMessage(t('success').replace('{credit}', String(result.credit)));
      setCampaign(current => current?.id === campaign.id ? { ...current, claim: result.claim } : current);
    } catch (error) {
      if (alive.current) {
        const claimed = error instanceof CampaignClaimError && error.code === 'claimed';
        setMessage(t(claimed ? 'claimed' : 'failed'));
        // 已领取是平台的权威结果；即使随后断网，也不能重新放开领取按钮或虚增累计积分。
        if (claimed) setCampaign(current => current?.id === campaign.id && current.claim
          ? { ...current, claim: { ...current.claim, can_claim: false, claimed_today: true, reason: 'claimed' } } : current);
      }
    } finally {
      claiming.current = false;
      if (alive.current) { setBusy(false); notifyCreditRefresh(); }
    }
  };
  if (hidden) return null;
  if (!campaign || !isCampaignActive(campaign)) return message ? <p role="status" className="m-2 text-xs">{message}</p> : null;
  /** 有效外链直接使用浏览器链接，避免异步弹窗被拦截。 */
  const renderAction = (action: CampaignAction, index: number) => {
    if (action.type === 'none') return null;
    const url = campaignActionUrl(campaign, action, base);
    const className = 'rounded-lg border border-claude-accent/30 px-2.5 py-1.5 text-xs text-claude-accent disabled:opacity-50';
    if (url) return <a key={index} href={url} target="_blank" rel="noopener noreferrer" className={className}>{action.label || t('details')}</a>;
    if (action.type !== 'claim' && action.type !== 'route') return null;
    const disabled = busy || (action.type === 'claim' && !campaign.claim?.can_claim);
    const label = action.type === 'claim' && campaign.claim?.claimed_today ? t('claimed') : action.label || t('claim');
    return <button key={index} type="button" className={className} disabled={disabled} onClick={() => void perform(action)}>{label}</button>;
  };
  return <section aria-label={t('title')} data-testid={`campaign-${slot}`} className="m-1 rounded-xl border border-claude-accent/20 bg-claude-accent/5 p-3 text-claude-text dark:text-claude-darkText">
    <div className="flex items-start gap-2"><h3 className="min-w-0 flex-1 break-words text-sm font-semibold">{campaign.title}</h3>
      {campaign.dismissible && <button type="button" aria-label={t('close')} onClick={dismiss} className="px-1 text-sm">×</button>}</div>
    {campaign.badge && <p className="mt-1 text-xs text-claude-accent">{campaign.badge}</p>}
    <p className="mt-2 break-words text-sm">{campaign.headline}</p>
    <p className="mt-1 break-words text-xs opacity-75">{campaign.subline}</p>
    {campaign.claim && campaign.claim.claimed_days > 0 && <p className="mt-1 text-xs">{t('total').replace('{credit}', String(campaign.claim.claimed_total_credit))}</p>}
    {message && <p role="status" className="mt-2 text-xs">{message}</p>}
    <div className="mt-2 flex flex-wrap gap-2">{[campaign.primary_action, campaign.secondary_action].map(renderAction)}</div>
  </section>;
}
